import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, query } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatAmount, formatDate } from "../../lib/format";
import type { AgeingBucket, Customer, Invoice, ListResponse } from "../../lib/types";
import { toMinor } from "./PaymentLinks";
import {
  Button,
  CopyButton,
  EmptyState,
  ErrorNotice,
  Field,
  Modal,
  PageHeader,
  Pagination,
  SelectField,
  Sheet,
  SheetHeader,
  Skeleton,
  StatusPill,
  Table,
  Td,
  TextAreaField,
  Th,
  Tr,
} from "../../components/ui";

const PAGE_SIZE = 25;

const AGEING_LABELS: Record<AgeingBucket["bucket"], string> = {
  not_yet_due: "Not yet due",
  "1_30": "1–30 days late",
  "31_60": "31–60 days late",
  "61_90": "61–90 days late",
  over_90: "Over 90 days",
};

/**
 * Invoices is the receivables page.
 *
 * An invoice here is a document, not a request: once issued it cannot be edited, only
 * voided and reissued, because the customer already holds a copy of what it said. The
 * ageing summary above the list is the thing a merchant chasing payment actually opens
 * the page for.
 */
export function Invoices() {
  const { mode } = useAuth();
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState("");
  const [composing, setComposing] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);

  const invoices = useQuery({
    queryKey: ["invoices", mode, offset, status],
    queryFn: () =>
      api.get<ListResponse<Invoice>>(
        `/api/dashboard/invoices${query({ mode, limit: PAGE_SIZE, offset, status })}`,
      ),
  });

  const ageing = useQuery({
    queryKey: ["invoice-ageing", mode],
    queryFn: () =>
      api.get<{ buckets: AgeingBucket[] | null }>(`/api/dashboard/invoices/ageing${query({ mode })}`),
  });

  const rows = invoices.data?.data ?? [];
  const buckets = (ageing.data?.buckets ?? []).filter((b) => b.count > 0);

  return (
    <>
      <PageHeader
        eyebrow="Billing"
        title="Invoices"
        description="Issue an invoice and your customer gets a page they can pay on. What you are owed, and how late it is, is summarised below."
        actions={<Button variant="primary" onClick={() => setComposing(true)}>New invoice</Button>}
      />

      {buckets.length > 0 && (
        <Sheet className="settle settle-1 mb-6 overflow-hidden">
          <SheetHeader title="Outstanding" description="What you are owed, by how late it is" />
          <ul className="grid divide-y divide-rule sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-5">
            {buckets.map((bucket) => (
              <li
                key={`${bucket.bucket}-${bucket.currency}`}
                className="px-5 py-4 lg:border-r lg:border-rule lg:last:border-r-0"
              >
                <p className="label">{AGEING_LABELS[bucket.bucket]}</p>
                <p
                  className={`mt-1 font-display text-[22px] tabular ${
                    bucket.bucket === "over_90" || bucket.bucket === "61_90"
                      ? "text-sienna"
                      : "text-ink-strong"
                  }`}
                >
                  {bucket.currency} {formatAmount(bucket.amount_minor)}
                </p>
                <p className="mt-0.5 text-[12px] text-ink-muted">
                  {bucket.count} invoice{bucket.count === 1 ? "" : "s"}
                </p>
              </li>
            ))}
          </ul>
        </Sheet>
      )}

      <Sheet className="settle settle-2 overflow-hidden">
        <SheetHeader
          title="All invoices"
          actions={
            <SelectField
              label=""
              aria-label="Filter by status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setOffset(0);
              }}
              options={[
                { value: "", label: "All statuses" },
                { value: "draft", label: "Draft" },
                { value: "open", label: "Open" },
                { value: "partially_paid", label: "Part paid" },
                { value: "overdue", label: "Overdue" },
                { value: "paid", label: "Paid" },
                { value: "void", label: "Void" },
              ]}
              className="w-40"
            />
          }
        />

        {invoices.isLoading ? (
          <Skeleton rows={6} />
        ) : invoices.error ? (
          <ErrorNotice error={invoices.error} className="m-5" />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No invoices yet"
            description="Draft one now — you can change it freely until you issue it."
            action={
              <Button variant="primary" onClick={() => setComposing(true)}>
                New invoice
              </Button>
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Number</Th>
                  <Th>Customer</Th>
                  <Th align="right">Total</Th>
                  <Th align="right">Outstanding</Th>
                  <Th>Due</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((invoice) => (
                  <Tr key={invoice.id} onClick={() => setViewing(invoice.id)}>
                    <Td>
                      <span className="font-mono text-[12.5px] text-ink-strong">{invoice.number}</span>
                      {invoice.title && (
                        <span className="mt-0.5 block text-[12px] text-ink-muted">{invoice.title}</span>
                      )}
                    </Td>
                    <Td className="text-ink-muted">{invoice.customer_name || "—"}</Td>
                    <Td align="right" className="tabular">
                      {invoice.currency} {formatAmount(invoice.total.minor)}
                    </Td>
                    <Td align="right" className="tabular">
                      {invoice.outstanding.minor > 0 ? (
                        <span className={invoice.overdue ? "text-sienna" : undefined}>
                          {invoice.currency} {formatAmount(invoice.outstanding.minor)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </Td>
                    <Td className="text-ink-muted">{invoice.due_at ? formatDate(invoice.due_at) : "—"}</Td>
                    <Td>
                      <StatusPill status={invoice.overdue ? "overdue" : invoice.status} />
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              total={invoices.data?.total ?? 0}
              limit={PAGE_SIZE}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </Sheet>

      <ComposeInvoice
        open={composing}
        mode={mode}
        onClose={() => setComposing(false)}
        onCreated={(invoice) => {
          setComposing(false);
          setViewing(invoice.id);
          queryClient.invalidateQueries({ queryKey: ["invoices"] });
        }}
      />

      <InvoiceDetail
        invoiceId={viewing}
        onClose={() => setViewing(null)}
        onChanged={() => {
          queryClient.invalidateQueries({ queryKey: ["invoices"] });
          queryClient.invalidateQueries({ queryKey: ["invoice-ageing"] });
        }}
      />
    </>
  );
}

function InvoiceDetail({
  invoiceId,
  onClose,
  onChanged,
}: {
  invoiceId: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();

  const invoice = useQuery({
    queryKey: ["invoice", invoiceId],
    queryFn: () => api.get<Invoice>(`/api/dashboard/invoices/${invoiceId}`),
    enabled: invoiceId !== null,
  });

  const act = useMutation({
    mutationFn: (action: "issue" | "void" | "write-off") =>
      api.post<Invoice>(`/api/dashboard/invoices/${invoiceId}/${action}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice", invoiceId] });
      onChanged();
    },
  });

  const data = invoice.data;

  return (
    <Modal
      open={invoiceId !== null}
      onClose={onClose}
      title={data ? `Invoice ${data.number}` : "Invoice"}
      description={data?.customer_name ? `For ${data.customer_name}` : undefined}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          {data?.status === "draft" && (
            <Button variant="primary" loading={act.isPending} onClick={() => act.mutate("issue")}>
              Issue invoice
            </Button>
          )}
          {data && data.status !== "draft" && data.status !== "paid" && data.status !== "void" && (
            <Button variant="danger" loading={act.isPending} onClick={() => act.mutate("void")}>
              Void
            </Button>
          )}
        </>
      }
    >
      {invoice.isLoading ? (
        <Skeleton rows={4} />
      ) : invoice.error ? (
        <ErrorNotice error={invoice.error} />
      ) : data ? (
        <div className="grid gap-4">
          <ErrorNotice error={act.error} />

          <div className="flex items-center justify-between">
            <StatusPill status={data.overdue ? "overdue" : data.status} />
            {data.due_at && (
              <span className="text-[12.5px] text-ink-muted">Due {formatDate(data.due_at)}</span>
            )}
          </div>

          <Table>
            <thead>
              <tr>
                <Th>Description</Th>
                <Th align="right">Qty</Th>
                <Th align="right">Unit</Th>
                <Th align="right">Amount</Th>
              </tr>
            </thead>
            <tbody>
              {data.line_items.map((item) => (
                <Tr key={item.id}>
                  <Td>{item.description}</Td>
                  <Td align="right" className="tabular">{item.quantity}</Td>
                  <Td align="right" className="tabular">{formatAmount(item.unit_price.minor)}</Td>
                  <Td align="right" className="tabular">{formatAmount(item.amount.minor)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>

          <dl className="ml-auto w-full max-w-[16rem] text-[13px]">
            <Total label="Subtotal" value={`${data.currency} ${formatAmount(data.subtotal.minor)}`} />
            {data.tax.minor > 0 && (
              <Total
                label={`Tax (${(data.tax_rate_bps / 100).toFixed(2).replace(/\.?0+$/, "")}%)`}
                value={`${data.currency} ${formatAmount(data.tax.minor)}`}
              />
            )}
            <Total
              label="Total"
              value={`${data.currency} ${formatAmount(data.total.minor)}`}
              strong
            />
            {data.paid.minor > 0 && (
              <Total label="Paid" value={`− ${data.currency} ${formatAmount(data.paid.minor)}`} />
            )}
            {data.outstanding.minor > 0 && data.status !== "draft" && (
              <Total
                label="Outstanding"
                value={`${data.currency} ${formatAmount(data.outstanding.minor)}`}
                strong
              />
            )}
          </dl>

          {data.payment_url && (
            <div className="rounded-md border border-rule-strong bg-paper-sunk px-4 py-3">
              <p className="label mb-1.5">Send this to your customer</p>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate font-mono text-[12.5px] text-ink-strong">
                  {data.payment_url}
                </code>
                <CopyButton value={data.payment_url} />
              </div>
            </div>
          )}

          {data.notes && <p className="text-[13px] text-ink-muted">{data.notes}</p>}
        </div>
      ) : null}
    </Modal>
  );
}

function Total({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div
      className={`flex justify-between gap-6 py-1 ${
        strong ? "border-t border-rule-strong pt-2 font-medium text-ink-strong" : "text-ink-muted"
      }`}
    >
      <dt>{label}</dt>
      <dd className="tabular">{value}</dd>
    </div>
  );
}

interface DraftLine {
  description: string;
  quantity: string;
  unitPrice: string;
}

function ComposeInvoice({
  open,
  mode,
  onClose,
  onCreated,
}: {
  open: boolean;
  mode: string;
  onClose: () => void;
  onCreated: (invoice: Invoice) => void;
}) {
  const [customerId, setCustomerId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [taxRate, setTaxRate] = useState("0");
  const [dueAt, setDueAt] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([
    { description: "", quantity: "1", unitPrice: "" },
  ]);

  const customers = useQuery({
    queryKey: ["customers", "all"],
    queryFn: () => api.get<ListResponse<Customer>>("/api/dashboard/customers?limit=200"),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<Invoice>("/api/dashboard/invoices", {
        mode,
        customer_id: customerId || null,
        currency,
        title,
        notes,
        tax_rate_bps: Math.round(Number(taxRate || 0) * 100),
        // The date input gives a bare day; the API wants an instant, and end of day is
        // what a merchant means by "due on the 30th".
        due_at: dueAt ? `${dueAt}T23:59:59Z` : null,
        line_items: lines
          .filter((line) => line.description.trim() !== "")
          .map((line) => ({
            description: line.description,
            quantity_milli: Math.round(Number(line.quantity || 0) * 1000),
            unit_price_minor: toMinor(line.unitPrice) ?? 0,
          })),
      }),
    onSuccess: onCreated,
  });

  const updateLine = (index: number, patch: Partial<DraftLine>) =>
    setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));

  // The running total is computed here so a merchant sees what they are about to send
  // before they send it; the server recomputes it from the same integer arithmetic.
  const subtotal = lines.reduce((sum, line) => {
    const quantityMilli = Math.round(Number(line.quantity || 0) * 1000);
    const unit = toMinor(line.unitPrice) ?? 0;
    return sum + Math.round((quantityMilli * unit) / 1000);
  }, 0);
  const tax = Math.round((subtotal * Math.round(Number(taxRate || 0) * 100)) / 10_000);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New invoice"
      description="Draft it now and issue it when you are ready. Nothing is sent until you issue."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>
            Save draft
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <ErrorNotice error={create.error} />

        <SelectField
          label="Customer"
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          hint="An invoice must be addressed to someone before it can be issued."
          options={[
            { value: "", label: "Choose a customer…" },
            ...(customers.data?.data ?? []).map((c) => ({
              value: c.id,
              label: c.display_name,
            })),
          ]}
        />

        <Field
          label="What is this for?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="August advisory"
        />

        <fieldset className="grid gap-2 rounded-md border border-rule-strong px-4 py-3">
          <legend className="label px-1">Lines</legend>
          {lines.map((line, index) => (
            <div key={index} className="grid gap-2 sm:grid-cols-[1fr_4.5rem_6rem_auto]">
              <Field
                label={index === 0 ? "Description" : ""}
                aria-label={`Line ${index + 1} description`}
                value={line.description}
                onChange={(e) => updateLine(index, { description: e.target.value })}
                placeholder="Advisory"
              />
              <Field
                label={index === 0 ? "Qty" : ""}
                aria-label={`Line ${index + 1} quantity`}
                inputMode="decimal"
                value={line.quantity}
                onChange={(e) => updateLine(index, { quantity: e.target.value })}
              />
              <Field
                label={index === 0 ? "Unit price" : ""}
                aria-label={`Line ${index + 1} unit price`}
                inputMode="decimal"
                value={line.unitPrice}
                onChange={(e) => updateLine(index, { unitPrice: e.target.value })}
                placeholder="150.00"
              />
              <div className={index === 0 ? "flex items-end" : "flex items-start"}>
                <Button
                  size="sm"
                  variant="ghost"
                  type="button"
                  aria-label={`Remove line ${index + 1}`}
                  disabled={lines.length === 1}
                  onClick={() => setLines((c) => c.filter((_, i) => i !== index))}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
          <Button
            size="sm"
            type="button"
            className="justify-self-start"
            onClick={() => setLines((c) => [...c, { description: "", quantity: "1", unitPrice: "" }])}
          >
            Add line
          </Button>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            label="Currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            options={[
              { value: "USD", label: "USD" },
              { value: "ZWG", label: "ZWG" },
            ]}
          />
          <Field
            label="Tax rate (%)"
            inputMode="decimal"
            value={taxRate}
            onChange={(e) => setTaxRate(e.target.value)}
            hint="Zimbabwean VAT is 15."
          />
          <Field
            label="Due date"
            type="date"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
          />
        </div>

        <dl className="ml-auto w-full max-w-[16rem] text-[13px]">
          <Total label="Subtotal" value={`${currency} ${formatAmount(subtotal)}`} />
          {tax > 0 && <Total label="Tax" value={`${currency} ${formatAmount(tax)}`} />}
          <Total label="Total" value={`${currency} ${formatAmount(subtotal + tax)}`} strong />
        </dl>

        <TextAreaField
          label="Notes"
          hint="Shown to the customer on the invoice."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
